import { create } from "axios";
import { useCallback, useMemo, type ReactNode } from "react";

import { useAuthentication, useToken } from "../../hooks";

import { AxiosContext } from "./axios.context";

interface AxiosProviderProps {
	readonly children: ReactNode;
}

export const AxiosProvider = ({ children }: AxiosProviderProps) => {
	const { logout } = useAuthentication();
	const { bearerToken } = useToken();

	const createAxiosClient = useCallback(() => {
		if (bearerToken) {
			return create({
				headers: {
					Authorization: bearerToken,
				},
			});
		}

		return create();
	}, [bearerToken]);

	const value = useMemo(() => {
		const client = createAxiosClient();

		client.interceptors.response.use(
			(response) => {
				if (response.status === 401) {
					logout();
				}

				return response;
			},
			(error) => {
				if (error.response && error.response.status === 401) {
					logout();
				}

				return Promise.reject(error);
			},
		);

		return client;
	}, [createAxiosClient, logout]);

	return <AxiosContext.Provider value={value}>{children}</AxiosContext.Provider>;
};
